<template>
  <f-modal
    ref="note-popup-modal"
    :class="{ 'is-visible': isVisible }"
    @close="handleClose">
    <f-modal-header>
      <h3 class="heading-medium">
        <template v-if="isForPayment">
          {{ $t('promotions.paymentInformation') }}
        </template>
        <template v-else-if="isEditMode">
          {{ $t('promotions.bidOnPromotion') }}
        </template>
        <template v-else>
          {{ $t('promotions.beginPromotionAuction') }}
        </template>
        <template v-if="loaded">- {{ data.name }}</template>
      </h3>
    </f-modal-header>

    <f-modal-body class="relative">
      <f-alert
        v-if="loadError"
        visible
        theme="warning">
        <span>
          {{ $t('ui.dataLoadError.dataErrorMess') }} <a href="javascript:window.location.reload();" class="lnk lnk-alert">{{ $t('ui.dataLoadError.resetPageButton') }}</a> {{ $t('ui.dataLoadError.forTryAgain') }}
        </span>
      </f-alert>
      <template v-else>
        <div
          v-if="!loaded"
          class="loading-overlay">
          <div class="loading-spinner-wrapper">
            <div class="loading-spinner pb-3 pt-3">
              <span class="loading-spinner-content">
                {{ $t('ui.loadingDataInProgress') }}&hellip;
              </span>
            </div>
          </div>
        </div>

        <div v-if="isForPayment">
          <p class="text-size-medium">{{ $t('promotions.costOfPromotion') }}: <strong>{{ data.payment_price }} PLN</strong></p>
          <a
            class="f-btn f-btn-primary inline-block ml-auto"
            :href="data.payment_url"
            target="_blank"
            rel="nofollow noopener noreferrer">
            {{ $t('promotions.proceedToPayment') }}
          </a>
        </div>
        <validation-observer
          v-else
          ref="promotion-bid-popup-form"
          tag="form">

          <div class="form-group mb-0">
            <label for="price">{{ $t('promotions.amount') }}</label>

            <validation-provider
              :rules="'required|min_value:' + minBidValue"
              name="price"
              v-slot="{ errors }">
              <f-input-wrapper append="PLN">
                <f-input
                  v-model="data.price"
                  :min="minBidValue.toString()"
                  step="1"
                  type="number"
                  :state="errors.length ? 'invalid' : null"/>
              </f-input-wrapper>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>
        </validation-observer>
      </template>

    </f-modal-body>
    <f-modal-footer>
      <div class="flex flex-justify-content-between">
        <f-button
          class="ml-0"
          theme="danger"
          @click.prevent="handleClose">
          {{ $t('ui.cancel') }}
        </f-button>
        <f-button
          v-if="!isForPayment && !loadError"
          class="ml-auto"
          theme="primary"
          :disabled="submitInProgress"
          @click.prevent="validateForm">
          <div
            v-if="submitInProgress"
            class="loading-spinner"
            role="status">
          </div>
          {{ $t('promotions.placeABid') }}
        </f-button>
      </div>
    </f-modal-footer>
  </f-modal>
</template>
<script>

import FormUtils from '@/utils/FormUtils.js';

export default {
  name: 'promotion-bid-popup',
  computed: {
    isEditMode () {
      if (!this.loaded) {
        return false;
      }

      return !!this.data.auction_id && this.data.status !== 'OCCUPIED';
    },
    isForPayment () {
      if (!this.loaded) {
        return false;
      }

      return this.data.status === 'OCCUPIED' && !!this.data.waiting_payment;
    },
    minBidValue () {
      if (!this.loaded || this.data.status === 'OCCUPIED') {
        return 0;
      }

      if (this.data.status === 'FREE') {
        return Number(this.data.minimal_price);
      }

      return Number(this.data.maximal_price) + 1;
    }
  },
  data() {
    return {
      actionInProgress: false,
      data: {
        auction_id: 0,
        current_user_presentation: false,
        id: 0,
        maximal_price: 0,
        minimal_price: 0,
        name: '',
        payment_price: 0,
        payment_url: null,
        price: 0,
        status: null,
        waiting_payment: false
      },
      isVisible: false,
      loaded: false,
      loadError: false,
      submitInProgress: false
    };
  },
  mounted () {
    this.$bus.$on('promotion-bid-popup-show', this.openPopup);
  },
  methods: {
    loadItemData (promotionSlotID) {
      FormUtils.loadItemData(this, {
        id: promotionSlotID,
        method: 'get',
        endpointBase: '/api/adviser/slots/item/',
        instanceFields: [
          'auction_id',
          'current_user_presentation',
          'id',
          'maximal_price',
          'minimal_price',
          'name',
          'payment_price',
          'payment_url',
          'status',
          'waiting_payment'
        ],
        loadedKey: 'loaded',
        successAction: (response) => {
          if (response.status === 'FREE') {
            this.data.price = Number(response.minimal_price);
          } else if (response.status === 'AUCTION') {
            this.data.price = Number(response.maximal_price) + 1;
          }
        }
      });
    },
    handleClose() {
      document.body.classList.remove('no-scroll');
      this.isVisible = false;
      this.loaded = false;
      this.loadError = false;
    },
    openPopup(promotionSlotID) {
      this.loadItemData(promotionSlotID);

      setTimeout(() => {
        this.isVisible = true;
        this.submitInProgress = false;
        document.body.classList.add('no-scroll');
        if (this.$refs['promotion-bid-popup-form']) {
          this.$refs['promotion-bid-popup-form'].reset();
        }
      }, 0);
    },
    validateForm () {
      FormUtils.validate(this.$refs['promotion-bid-popup-form'], this.submitForm);
    },
    submitForm () {
      let formDataToSend = this.prepareFormDataToSend();
      let endpointUrl = this.isEditMode ? '/api/adviser/slots/auctions/add-offer' : '/api/adviser/slots/auctions/start';

      FormUtils.submit(this, {
        endpoint: endpointUrl,
        formData: formDataToSend,
        successTitle: this.$t('promotions.placeBidSuccessTitle'),
        successText: this.$t('promotions.placeBidSuccessText'),
        errorTitle: this.$t('ui.errorOccurred'),
        errorText: this.$t('promotions.placeBidErrorText'),
        successAction: () => {
          this.$bus.$emit('promotion-slots-list-reload');
          this.handleClose();
        }
      });
    },
    prepareFormDataToSend () {
      let formDataToSend = {
        price: this.data.price
      };

      if (this.isEditMode) {
        formDataToSend.auction_id = this.data.auction_id;
      } else {
        formDataToSend.slot_id = this.data.id;
      }

      return formDataToSend;
    }
  },
  beforeDestroy () {
    this.$bus.$off('promotion-bid-popup-show', this.openPopup);
  }
}
</script>
